import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as FaIcons from 'react-icons/fa';
import { CategoryFormProps, ColorButtonProps, IconButtonProps, MarkerShape } from '../../types/category';
import { UNIFIED_ICONS } from '../../config/unifiedIcons';
import Button from '../common/Button';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  &:focus-within {
    outline: none;
  }
`;

const Title = styled.h2`
  margin: 0 0 20px;
  color: #333;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #444;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4285f4;
  }
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-top: 8px;
`;

const ColorButton = styled.button<ColorButtonProps>`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.$isSelected ? '#000' : 'transparent'};
  border-radius: 4px;
  background-color: ${props => props.$bgColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  position: relative;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #3498db;
  }

  &:focus:not(:focus-visible) {
    box-shadow: none;
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    color: ${props => {
      // Calculer la luminosité de la couleur pour déterminer si l'icône doit être noire ou blanche
      const hex = props.$bgColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000' : '#fff';
    }};
    opacity: ${props => props.$isSelected ? 1 : 0};
    font-size: 14px;
  }
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-top: 8px;
`;

const IconButton = styled.button<IconButtonProps>`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.$isSelected ? '#4285f4' : '#ddd'};
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  
  svg {
    font-size: 18px;
    color: ${props => props.$isSelected ? '#4285f4' : '#666'};
  }
  
  &:hover {
    border-color: #4285f4;
    svg {
      color: #4285f4;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.5);
  }
`;

const ShapeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
  }
`;

const ShapeButton = styled.button<{ $isSelected: boolean }>`
  width: 48px;
  height: 48px;
  border: 2px solid ${props => props.$isSelected ? '#4285f4' : '#ddd'};
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 6px;
  position: relative;
  
  svg {
    width: 100%;
    height: 100%;
    color: ${props => props.$isSelected ? '#4285f4' : '#666'};
  }
  
  &:hover {
    border-color: #4285f4;
    transform: scale(1.05);
    svg {
      color: #4285f4;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
`;

const colors = [
  '#2ecc71',  // Vert émeraude
  '#3498db',  // Bleu océan
  '#9b59b6',  // Violet améthyste
  '#e74c3c',  // Rouge corail
  '#f1c40f',  // Jaune soleil
  '#1abc9c',  // Turquoise
  '#e67e22',  // Orange mandarine
  '#34495e',  // Bleu nuit
  '#27ae60',  // Vert forêt
  '#2980b9',  // Bleu royal
  '#8e44ad',  // Violet royal
  '#c0392b'   // Rouge rubis
];

const CategoryForm: React.FC<CategoryFormProps> = ({ onClose, onSubmit, initialCategory, onDelete }) => {
  const [name, setName] = useState(initialCategory?.name || '');
  const [color, setColor] = useState(initialCategory?.color || colors[0]);
  const [selectedIcon, setSelectedIcon] = useState(initialCategory?.icon || '');
  const [markerShape, setMarkerShape] = useState<MarkerShape>(initialCategory?.markerShape || 'circle');
  const [errors, setErrors] = useState<{
    name?: string;
    color?: string;
    icon?: string;
  }>({});

  // Réinitialiser l'icône si on change de forme vers une forme non-circle
  const handleShapeChange = (newShape: MarkerShape) => {
    setMarkerShape(newShape);
    // Si on passe à une forme autre que circle, on peut réinitialiser l'icône
    // car elle ne sera pas utilisée
    if (newShape !== 'circle') {
      // On garde quand même l'icône sélectionnée au cas où l'utilisateur revient au cercle
    }
  };

  useEffect(() => {
    if (initialCategory) {
      setName(initialCategory.name);
      setColor(initialCategory.color);
      setSelectedIcon(initialCategory.icon);
      setMarkerShape(initialCategory.markerShape || 'circle');
      setErrors({});
    }
  }, [initialCategory]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    let isValid = true;

    // Validation du nom
    if (!name.trim()) {
      newErrors.name = 'Le nom est requis';
      isValid = false;
    } else if (name.trim().length < 3) {
      newErrors.name = 'Le nom doit contenir au moins 3 caractères';
      isValid = false;
    } else if (name.trim().length > 30) {
      newErrors.name = 'Le nom ne doit pas dépasser 30 caractères';
      isValid = false;
    }

    // Validation de la couleur
    if (!color) {
      newErrors.color = 'La couleur est requise';
      isValid = false;
    } else if (!colors.includes(color)) {
      newErrors.color = 'Veuillez sélectionner une couleur valide';
      isValid = false;
    }

    // Validation de l'icône (uniquement si markerShape est 'circle')
    if (markerShape === 'circle' && !selectedIcon) {
      newErrors.icon = 'L\'icône est requise';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const formData = {
      name,
      color,
      icon: selectedIcon || 'home', // Icône par défaut si non sélectionnée (pour les formes non-circle)
      markerShape
    };
    
    console.log('📝 Données du formulaire:', formData);

    onSubmit(formData);

    onClose();
  };

  const handleDelete = () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      onDelete?.(initialCategory?.id || '');
      onClose();
    }
  };

  // Convertir les icônes unifiées en options pour le sélecteur
  const iconOptions = Object.entries(UNIFIED_ICONS).map(([_key, value]) => ({
    icon: value.component,
    name: value.label
  }));

  // Options de formes de markers
  const markerShapes: Array<{ shape: MarkerShape; icon: React.ReactNode; label: string }> = [
    { 
      shape: 'circle', 
      icon: <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>,
      label: 'Cercle'
    },
    { 
      shape: 'house', 
      icon: <FaIcons.FaHome />,
      label: 'Maison'
    },
    { 
      shape: 'building', 
      icon: <FaIcons.FaBuilding />,
      label: 'Immeuble'
    },
    { 
      shape: 'person', 
      icon: <FaIcons.FaUser />,
      label: 'Personne'
    },
    { 
      shape: 'marker', 
      icon: <FaIcons.FaMapMarkerAlt />,
      label: 'Marqueur'
    }
  ];

  return (
    <Form onSubmit={handleSubmit} role="form" aria-label="Formulaire de catégorie">
      <Title>{initialCategory ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}</Title>
      
      <Field>
        <Label htmlFor="category-name">
          Nom de la catégorie
          {errors.name && <ErrorMessage role="alert" aria-live="polite">{errors.name}</ErrorMessage>}
        </Label>
        <Input
          id="category-name"
          type="text"
          value={name}
          onChange={e => {
            setName(e.target.value);
            if (errors.name) {
              setErrors(prev => ({ ...prev, name: undefined }));
            }
          }}
          placeholder="Ex: Maisons à vendre"
          required
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? "name-error" : undefined}
        />
      </Field>

      <Field>
        <Label id="shape-group">
          Forme du marker
        </Label>
        <ShapeGrid role="radiogroup" aria-labelledby="shape-group">
          {markerShapes.map(({ shape, icon, label }) => (
            <ShapeButton
              key={shape}
              type="button"
              $isSelected={markerShape === shape}
              onClick={() => handleShapeChange(shape)}
              title={label}
              role="radio"
              aria-checked={markerShape === shape}
              aria-label={label}
            >
              {icon}
            </ShapeButton>
          ))}
        </ShapeGrid>
      </Field>

      <Field>
        <Label id="color-group">
          Couleur
          {errors.color && <ErrorMessage role="alert" aria-live="polite">{errors.color}</ErrorMessage>}
        </Label>
        <ColorGrid role="radiogroup" aria-labelledby="color-group">
          {colors.map((colorOption) => (
            <ColorButton
              key={colorOption}
              type="button"
              $bgColor={colorOption}
              $isSelected={color === colorOption}
              onClick={() => {
                setColor(colorOption);
                if (errors.color) {
                  setErrors(prev => ({ ...prev, color: undefined }));
                }
              }}
              aria-label={`Couleur ${colorOption}`}
            >
              {color === colorOption && <FaIcons.FaCheck aria-hidden="true" />}
            </ColorButton>
          ))}
        </ColorGrid>
      </Field>

      {/* Afficher le sélecteur d'icône uniquement si la forme est "circle" */}
      {markerShape === 'circle' && (
        <Field>
          <Label id="icon-group">
            Icône
            {errors.icon && <ErrorMessage role="alert" aria-live="polite">{errors.icon}</ErrorMessage>}
          </Label>
          <IconGrid role="radiogroup" aria-labelledby="icon-group">
            {iconOptions.map(({ icon: Icon, name: iconName }) => (
              <IconButton
                key={iconName}
                type="button"
                $isSelected={selectedIcon === iconName}
                onClick={() => {
                  setSelectedIcon(iconName);
                  if (errors.icon) {
                    setErrors(prev => ({ ...prev, icon: undefined }));
                  }
                }}
                title={iconName}
                role="radio"
                aria-checked={selectedIcon === iconName}
                aria-label={iconName}
              >
                <Icon aria-hidden="true" />
              </IconButton>
            ))}
          </IconGrid>
        </Field>
      )}

      <ButtonGroup>
        <Button type="button" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" $variant="primary">
          {initialCategory ? 'Mettre à jour' : 'Créer'}
        </Button>
        {initialCategory && (
          <Button
            type="button"
            onClick={handleDelete}
            style={{ backgroundColor: '#dc2626', color: 'white', borderColor: '#dc2626' }}
          >
            Supprimer
          </Button>
        )}
      </ButtonGroup>
    </Form>
  );
};

export default CategoryForm;

